import { ArrowLeft, CalendarPlus, FileText, Filter, Pencil, Plus, RefreshCcw } from 'lucide-react';
import { memo, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { PageLayout } from '@/components/layout/PageLayout';
import { ActivityTab } from '@/components/loans/ActivityTab';
import { AiInsightsPanel } from '@/components/loans/AiInsightsPanel';
import { CashflowsTab } from '@/components/loans/CashflowsTab';
import { CaveatsTab } from '@/components/loans/CaveatsTab';
import { ChangeHistoryTab } from '@/components/loans/ChangeHistoryTab';
import { CommentsTab } from '@/components/loans/CommentsTab';
import { CommunicationsTab } from '@/components/loans/CommunicationsTab';
import { CovenantTab } from '@/components/loans/CovenantTab';
import { CreditPackModal } from '@/components/loans/CreditPackModal';
import { DealAnalysisTab } from '@/components/loans/DealAnalysisTab';
import { DocumentGeneratorPanel } from '@/components/loans/DocumentGeneratorPanel';
import { DocumentManagerTab } from '@/components/loans/DocumentManagerTab';
import { DocumentsTab } from '@/components/loans/DocumentsTab';
import { EditLoanModal } from '@/components/loans/EditLoanModal';
import { EmailTab } from '@/components/loans/EmailTab';
import { ExtensionModal } from '@/components/loans/ExtensionModal';
import { FeeScheduleTab } from '@/components/loans/FeeScheduleTab';
import { FundingTab } from '@/components/loans/FundingTab';
import { GuarantorTab } from '@/components/loans/GuarantorTab';
import { InterestTab } from '@/components/loans/InterestTab';
import { InvoiceTab } from '@/components/loans/InvoiceTab';
import { LedgerTab } from '@/components/loans/LedgerTab';
import { LoanOverviewTab } from '@/components/loans/LoanOverviewTab';
import { MarketResearchPanel } from '@/components/loans/MarketResearchPanel';
import { PaymentsTab } from '@/components/loans/PaymentsTab';
import { PerfectionTab } from '@/components/loans/PerfectionTab';
import { RateManagementTab } from '@/components/loans/RateManagementTab';
import { RestructureChainBanner } from '@/components/loans/RestructureChainBanner';
import { RestructureModal } from '@/components/loans/RestructureModal';
import { RestructureTab } from '@/components/loans/RestructureTab';
import { RiskScorePanel } from '@/components/loans/RiskScorePanel';
import { ScheduleTab } from '@/components/loans/ScheduleTab';
import { SecurityScheduleTab } from '@/components/loans/SecurityScheduleTab';
import { SettlementTab } from '@/components/loans/SettlementTab';
import { SignatureTab } from '@/components/loans/SignatureTab';
import { StatusTransitionModal } from '@/components/loans/StatusTransitionModal';
import { TrancheTab } from '@/components/loans/TrancheTab';
import { ValuationInsuranceTab } from '@/components/loans/ValuationInsuranceTab';
import { VariationTab } from '@/components/loans/VariationTab';
import { Spinner } from '@/components/ui';
import { ComponentErrorBoundary } from '@/components/ui';
import { Button } from '@/components/ui';
import { ErrorState } from '@/components/ui/ErrorState';
import { useLoan } from '@/hooks/use-loans';
import { useRecentLoans } from '@/hooks/use-recent-loans';
import { useSecurities } from '@/hooks/use-securities';
import { cn } from '@/lib/cn';
import { LOAN_STATUS_TRANSITIONS } from '@/lib/loan-status-transitions';
import type { LoanStatus } from '@/types';

const LOAN_DETAIL_CAPABILITY_KEYS = [
  'overview',
  // 'credit-pack',
  'insights',
  'activity',
  'changes',
  'deal',
  'risk',
  'security',
  'guarantors',
  'caveats',
  'perfection',
  'valuation',
  'research',
  'schedule',
  'interest',
  'payments',
  'ledger',
  'cashflows',
  'fees',
  'invoices',
  'funding',
  'tranches',
  'rates',
  'variations',
  'restructures',
  'covenants',
  'documents',
  'doc-manager',
  'docgen',
  'signatures',
  'settlement',
  'communications',
  'emails',
  'comments',
] as const;

type TabKey = (typeof LOAN_DETAIL_CAPABILITY_KEYS)[number];

type LoanDetailDisplayTab = {
  key: string;
  label: string;
  capabilities: readonly TabKey[];
  viewLabels?: Partial<Record<TabKey, string>>;
};

type LoanDetailSection = {
  key: string;
  label: string;
  defaultCapability: TabKey;
  tabs: readonly LoanDetailDisplayTab[];
};

const LOAN_DETAIL_SECTIONS: readonly LoanDetailSection[] = [
  {
    key: 'summary',
    label: 'Summary',
    defaultCapability: 'overview',
    tabs: [
      { key: 'overview', label: 'Overview', capabilities: ['overview'] },
      // { key: 'credit-pack', label: 'Credit Pack', capabilities: ['credit-pack'] },
      { key: 'insights', label: 'AI Insights', capabilities: ['insights'] },
      {
        key: 'activity',
        label: 'Activity',
        capabilities: ['activity', 'changes'],
        viewLabels: { activity: 'Timeline', changes: 'Changes' },
      },
    ],
  },
  {
    key: 'application-credit',
    label: 'Application & Credit',
    defaultCapability: 'deal',
    tabs: [
      { key: 'deal', label: 'Deal Analysis', capabilities: ['deal'] },
      { key: 'risk', label: 'Risk Score', capabilities: ['risk'] },
    ],
  },
  {
    key: 'security',
    label: 'Security',
    defaultCapability: 'security',
    tabs: [
      { key: 'security', label: 'Security', capabilities: ['security'] },
      { key: 'guarantors', label: 'Guarantors', capabilities: ['guarantors'] },
      { key: 'caveats', label: 'Caveats', capabilities: ['caveats'] },
      { key: 'perfection', label: 'Perfection', capabilities: ['perfection'] },
      { key: 'valuation', label: 'Val & Insurance', capabilities: ['valuation'] },
      { key: 'research', label: 'Research', capabilities: ['research'] },
    ],
  },
  {
    key: 'money',
    label: 'Money',
    defaultCapability: 'schedule',
    tabs: [
      { key: 'schedule', label: 'Schedule', capabilities: ['schedule'] },
      { key: 'interest', label: 'Interest', capabilities: ['interest'] },
      { key: 'payments', label: 'Payments', capabilities: ['payments'] },
      { key: 'ledger', label: 'Ledger', capabilities: ['ledger'] },
      { key: 'cashflows', label: 'Cashflows', capabilities: ['cashflows'] },
      { key: 'fees', label: 'Fees', capabilities: ['fees'] },
      { key: 'invoices', label: 'Invoices', capabilities: ['invoices'] },
    ],
  },
  {
    key: 'funding',
    label: 'Funding',
    defaultCapability: 'funding',
    tabs: [
      { key: 'funding', label: 'Funding', capabilities: ['funding'] },
      { key: 'tranches', label: 'Tranches', capabilities: ['tranches'] },
    ],
  },
  {
    key: 'structure-changes',
    label: 'Structure Changes',
    defaultCapability: 'rates',
    tabs: [
      { key: 'rates', label: 'Rates', capabilities: ['rates'] },
      {
        key: 'variations',
        label: 'Variations',
        capabilities: ['variations', 'restructures'],
        viewLabels: { variations: 'Variations', restructures: 'Restructures' },
      },
      { key: 'covenants', label: 'Covenants', capabilities: ['covenants'] },
    ],
  },
  {
    key: 'documents',
    label: 'Documents',
    defaultCapability: 'documents',
    tabs: [
      {
        key: 'documents',
        label: 'Documents',
        capabilities: ['documents', 'doc-manager'],
        viewLabels: { documents: 'Documents', 'doc-manager': 'Doc Manager' },
      },
      { key: 'docgen', label: 'Generate', capabilities: ['docgen'] },
      { key: 'signatures', label: 'E-Sign', capabilities: ['signatures'] },
    ],
  },
  {
    key: 'settlement',
    label: 'Settlement',
    defaultCapability: 'settlement',
    tabs: [{ key: 'settlement', label: 'Settlement', capabilities: ['settlement'] }],
  },
  {
    key: 'communication',
    label: 'Communication',
    defaultCapability: 'communications',
    tabs: [
      {
        key: 'communications',
        label: 'Comms',
        capabilities: ['communications', 'emails'],
        viewLabels: {
          communications: 'Comms',
          emails: 'Emails',
        },
      },
      { key: 'comments', label: 'Comments', capabilities: ['comments'] },
    ],
  },
] as const;

function isTabKey(value: string | null): value is TabKey {
  return !!value && (LOAN_DETAIL_CAPABILITY_KEYS as readonly string[]).includes(value);
}

function findSectionForCapability(tabKey: TabKey) {
  return (
    LOAN_DETAIL_SECTIONS.find(section =>
      section.tabs.some(tab => tab.capabilities.includes(tabKey)),
    ) ?? LOAN_DETAIL_SECTIONS[0]
  );
}

function findDisplayTabForCapability(section: LoanDetailSection, tabKey: TabKey) {
  return section.tabs.find(tab => tab.capabilities.includes(tabKey)) ?? section.tabs[0];
}

function LoanDetailInner() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedCapability = searchParams.get('tab');
  const normalizedRequestedCapability =
    requestedCapability === 'correspondence' ? 'emails' : requestedCapability;
  const activeCapability: TabKey = isTabKey(normalizedRequestedCapability)
    ? normalizedRequestedCapability
    : 'overview';

  const { data: loan, isLoading, isError } = useLoan(id!);
  const { data: securities } = useSecurities(id!);
  const { trackView } = useRecentLoans();
  const [transitionTarget, setTransitionTarget] = useState<LoanStatus | ''>('');

  // Track recently viewed loan
  useEffect(() => {
    if (loan) {
      trackView({
        id: loan.id,
        loanNumber: loan.loanNumber,
        borrowerName: loan.borrower?.name ?? 'Unknown',
        status: loan.status,
      });
    }
  }, [loan?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [showTransitionModal, setShowTransitionModal] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [restructureOpen, setRestructureOpen] = useState(false);
  const [creditPackOpen, setCreditPackOpen] = useState(false);
  const [extensionOpen, setExtensionOpen] = useState(false);

  const activeSection = findSectionForCapability(activeCapability);
  const activeDisplayTab = findDisplayTabForCapability(activeSection, activeCapability);
  const showDocumentToolbar = activeSection.key === 'documents' && activeCapability === 'documents';

  function setActiveCapability(next: TabKey) {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', next);
    setSearchParams(nextParams, { replace: true });
  }

  function setActiveSection(sectionKey: string) {
    const targetSection = LOAN_DETAIL_SECTIONS.find(section => section.key === sectionKey);
    if (targetSection) {
      setActiveCapability(targetSection.defaultCapability);
    }
  }

  function setActiveDisplayTab(displayKey: string) {
    const targetDisplayTab = activeSection.tabs.find(tab => tab.key === displayKey);
    if (targetDisplayTab) {
      setActiveCapability(targetDisplayTab.capabilities[0]);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96 text-gray-400">
        <Spinner size="lg" className="w-5 h-5 animate-spin mr-2" />
        Loading loan…
      </div>
    );
  }

  if (isError || !loan) {
    return (
      <ErrorState
        title="Loan not found"
        message="This loan may have been deleted or you may not have permission to view it."
        onRetry={() => window.location.reload()}
      />
    );
  }

  const allowedNext = LOAN_STATUS_TRANSITIONS[loan.status] ?? [];

  function handleTransitionSelect(status: LoanStatus) {
    setTransitionTarget(status);
  }

  function fmtCurrency(v: string) {
    const n = parseFloat(v);
    if (isNaN(n)) return v;
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
    }).format(n);
  }

  return (
    <PageLayout
      title={
        <div className="flex items-center gap-3 flex-wrap">
          <Link
            to="/loans"
            className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
            aria-label="Back"
            title="Back"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div className="flex items-baseline gap-2">
            <span>{loan.borrower?.name || loan.loanNumber}</span>
            <span className="text-sm font-normal text-gray-500">({loan.loanNumber})</span>
          </div>
        </div>
      }
      headerAction={
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <Button
            variant="secondary"
            onClick={() => setCreditPackOpen(true)}
            className="w-fit flex items-center gap-2"
          >
            <FileText className="w-3 h-3 min-w-3.5 min-h-3.5" />
            Credit Pack
          </Button>
          <Button
            variant="secondary"
            onClick={() => setEditOpen(true)}
            className="w-fit flex items-center gap-2"
          >
            <Pencil className="w-3.5 h-3.5 min-w-3.5 min-h-3.5" />
            Edit
          </Button>
          {['CURRENT', 'DISBURSED', 'DELINQUENT_30', 'DELINQUENT_60', 'DELINQUENT_90'].includes(
            loan.status,
          ) && (
            <Button
              variant="secondary"
              onClick={() => setExtensionOpen(true)}
              className="w-fit flex items-center gap-2"
            >
              <CalendarPlus className="w-3.5 h-3.5 min-w-3.5 min-h-3.5" />
              Extend
              {(loan.extensions?.length ?? 0) > 0 && (
                <span className="text-[10px] bg-blue-200 text-blue-800 px-2.5 py-0.5 rounded-full font-bold ml-0.5">
                  {loan.extensions!.length}
                </span>
              )}
            </Button>
          )}
          {['CURRENT', 'DELINQUENT_30', 'DELINQUENT_60', 'DELINQUENT_90', 'DEFAULT'].includes(
            loan.status,
          ) && (
            <Button
              variant="secondary"
              onClick={() => setRestructureOpen(true)}
              className="w-fit flex items-center gap-2"
            >
              <RefreshCcw className="w-3.5 h-3.5 min-w-3.5 min-h-3.5" />
              Restructure
            </Button>
          )}
          {allowedNext.length > 0 && (
            <Button
              variant="primary"
              onClick={() => {
                setTransitionTarget('');
                setShowTransitionModal(true);
              }}
              className="w-fit"
            >
              Update Status
            </Button>
          )}
        </div>
      }
      headerBottomContent={
        <div className="space-y-4 pb-4">
          <div className="tab-scrollbar overflow-x-auto overflow-y-hidden">
            <div className="flex w-max min-w-max items-center gap-3">
              {LOAN_DETAIL_SECTIONS.map(section => {
                const isActive = section.key === activeSection.key;
                return (
                  <button
                    key={section.key}
                    type="button"
                    onClick={() => setActiveSection(section.key)}
                    className={cn(
                      'shrink-0 rounded-lg border px-4 py-2 text-sm font-semibold transition-colors',
                      isActive
                        ? 'border-gray-900 bg-gray-900 text-white shadow-sm'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:text-gray-900',
                    )}
                  >
                    {section.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex flex-col gap-3 border-t border-gray-200 pt-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="tab-scrollbar overflow-x-auto overflow-y-hidden">
              <div className="flex h-10 w-max min-w-max items-center gap-6">
                <div className="flex h-10 items-center gap-5">
                  {activeSection.tabs.map(tab => {
                    const isActive = tab.key === activeDisplayTab.key;
                    return (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => setActiveDisplayTab(tab.key)}
                        className={cn(
                          'flex h-10 items-center border-b-2 text-sm font-semibold transition-colors',
                          isActive
                            ? 'border-gray-900 text-gray-900'
                            : 'border-transparent text-gray-500 hover:text-gray-900',
                        )}
                      >
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
                {activeDisplayTab.capabilities.length > 1 && (
                  <>
                    <div className="h-6 w-px bg-gray-200" />
                    <div className="flex h-10 items-center gap-2">
                      {activeDisplayTab.capabilities.map(capability => {
                        const isActive = capability === activeCapability;
                        return (
                          <button
                            key={capability}
                            type="button"
                            onClick={() => setActiveCapability(capability)}
                            className={cn(
                              'flex h-9 items-center rounded-md px-4 text-sm font-semibold transition-colors',
                              isActive
                                ? 'bg-gray-900 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900',
                            )}
                          >
                            {activeDisplayTab.viewLabels?.[capability] ?? capability}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
            {showDocumentToolbar && (
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  className="flex h-10 items-center gap-2 rounded-md border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                >
                  <Filter className="h-4 w-4" />
                  Filters
                </button>
                <button
                  type="button"
                  onClick={() =>
                    window.dispatchEvent(new CustomEvent('loan-documents:open-upload'))
                  }
                  className="flex h-10 items-center gap-2 rounded-md bg-gray-900 px-5 text-sm font-semibold text-white hover:bg-gray-800"
                >
                  <Plus className="h-4 w-4" />
                  Upload
                </button>
              </div>
            )}
          </div>
        </div>
      }
      contentClassName="space-y-4 pb-28 sm:pb-32"
    >
      {/* Restructure chain banner */}
      <RestructureChainBanner loanId={id!} />

      {/* Tab content */}
      <div className="w-full">
        {activeCapability === 'overview' && (
          <ComponentErrorBoundary name="Overview Tab">
            <LoanOverviewTab loan={loan} />
          </ComponentErrorBoundary>
        )}
        {/* {activeCapability === 'credit-pack' && ( */}
        {/*   <ComponentErrorBoundary name="Credit Pack Tab"> */}
        {/*     <div className="rounded-lg border border-gray-200 bg-white p-6"> */}
        {/*       <div className="max-w-2xl space-y-3"> */}
        {/*         <h2 className="text-lg font-semibold text-gray-900">Credit Pack</h2> */}
        {/*         <p className="text-sm text-gray-600"> */}
        {/*           Generate or review the full credit pack for this loan from its dedicated Summary */}
        {/*           tab. */}
        {/*         </p> */}
        {/*         <Button */}
        {/*           variant="primary" */}
        {/*           onClick={() => setCreditPackOpen(true)} */}
        {/*           className="!w-auto" */}
        {/*         > */}
        {/*           Open Credit Pack */}
        {/*         </Button> */}
        {/*       </div> */}
        {/*     </div> */}
        {/*   </ComponentErrorBoundary> */}
        {/* )} */}
        {activeCapability === 'security' && (
          <ComponentErrorBoundary name="Security Tab">
            <SecurityScheduleTab loanId={id!} />
          </ComponentErrorBoundary>
        )}
        {activeCapability === 'research' && (
          <ComponentErrorBoundary name="Research Tab">
            <MarketResearchPanel securities={securities ?? []} />
          </ComponentErrorBoundary>
        )}
        {activeCapability === 'deal' && (
          <ComponentErrorBoundary name="Deal Analysis Tab">
            <DealAnalysisTab loanId={id!} />
          </ComponentErrorBoundary>
        )}
        {activeCapability === 'tranches' && <TrancheTab loanId={id!} />}
        {activeCapability === 'interest' && <InterestTab loanId={id!} />}
        {activeCapability === 'payments' && <PaymentsTab loanId={id!} />}
        {activeCapability === 'ledger' && <LedgerTab loanId={id!} />}
        {activeCapability === 'schedule' && <ScheduleTab loanId={id!} />}
        {activeCapability === 'documents' && <DocumentsTab loanId={id!} />}
        {activeCapability === 'settlement' && <SettlementTab loanId={id!} loan={loan} />}
        {activeCapability === 'communications' && <CommunicationsTab loanId={id!} />}
        {activeCapability === 'caveats' && <CaveatsTab loanId={id!} />}
        {activeCapability === 'covenants' && <CovenantTab loanId={id!} />}
        {activeCapability === 'guarantors' && <GuarantorTab loanId={id!} />}
        {activeCapability === 'restructures' && <RestructureTab loanId={id!} />}
        {activeCapability === 'risk' && <RiskScorePanel loanId={id!} />}
        {activeCapability === 'signatures' && <SignatureTab loanId={id!} />}
        {activeCapability === 'fees' && <FeeScheduleTab loanId={id!} />}
        {activeCapability === 'doc-manager' && (
          <DocumentManagerTab loanId={id!} legalMatterRef={loan?.legalMatterRef} />
        )}
        {activeCapability === 'docgen' && (
          <DocumentGeneratorPanel
            loanId={id!}
            loanNumber={loan.loanNumber}
            extensions={loan.extensions?.map((e: any) => ({
              id: e.id,
              extensionNumber: e.extensionNumber,
            }))}
          />
        )}
        {activeCapability === 'changes' && <ChangeHistoryTab loanId={id!} />}
        {activeCapability === 'invoices' && (
          <InvoiceTab loanId={id!} loanNumber={loan.loanNumber} />
        )}
        {activeCapability === 'perfection' && <PerfectionTab loanId={id!} />}
        {activeCapability === 'variations' && <VariationTab loanId={id!} loan={loan} />}
        {activeCapability === 'rates' && (
          <RateManagementTab
            loanId={id!}
            currentRate={Number(loan.interestRate)}
            rateType={(loan as any).rateType}
            baseRateName={(loan as any).baseRateName}
            margin={(loan as any).margin}
            nextReviewDate={(loan as any).nextReviewDate}
          />
        )}
        {activeCapability === 'valuation' && (
          <>
            {!securities || securities.length === 0 ? (
              <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                <div className="mx-auto w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                  <svg
                    className="w-6 h-6 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                </div>
                <h3 className="text-sm font-semibold text-gray-900 mb-1">No securities attached</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Add a security on the <strong>Security</strong> tab first to manage valuations and
                  insurance policies.
                </p>
                <Button
                  variant="secondary"
                  onClick={() => setActiveCapability('security')}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 rounded-md hover: transition-colors"
                >
                  Go to Security Tab
                </Button>
              </div>
            ) : (
              securities.map((sec: any) => (
                <ValuationInsuranceTab
                  key={sec.id}
                  securityId={sec.id}
                  securityType={sec.type}
                  currentValue={sec.currentValue}
                  loanAmount={Number(loan.principal)}
                  loanId={id!}
                />
              ))
            )}
          </>
        )}
        {activeCapability === 'emails' && (
          <EmailTab
            loanId={id!}
            loan={loan}
            borrowerEmail={loan.borrower?.email}
            borrowerName={loan.borrower?.name}
          />
        )}
        {activeCapability === 'cashflows' && <CashflowsTab loanId={id!} />}
        {activeCapability === 'funding' && <FundingTab loanId={id!} />}
        {activeCapability === 'comments' && <CommentsTab loanId={id!} />}
        {activeCapability === 'activity' && <ActivityTab loanId={id!} />}
        {activeCapability === 'insights' && <AiInsightsPanel loan={loan} />}
      </div>

      <div aria-hidden="true" className="h-28 sm:h-36" />

      {/* Edit loan modal */}
      <EditLoanModal loan={loan} open={editOpen} onClose={() => setEditOpen(false)} />

      {/* Restructure modal */}
      <RestructureModal
        loan={loan}
        open={restructureOpen}
        onClose={() => setRestructureOpen(false)}
      />

      {/* Extension modal */}
      <ExtensionModal loan={loan} open={extensionOpen} onClose={() => setExtensionOpen(false)} />

      {/* Credit Pack modal */}
      <CreditPackModal
        loanId={id!}
        loanNumber={loan.loanNumber}
        open={creditPackOpen}
        onClose={() => setCreditPackOpen(false)}
      />

      <StatusTransitionModal
        open={showTransitionModal}
        onClose={() => {
          setShowTransitionModal(false);
          setTransitionTarget('');
        }}
        loanId={id!}
        loanNumber={loan.loanNumber}
        currentStatus={loan.status}
        targetStatus={transitionTarget || undefined}
        availableStatuses={allowedNext}
        onSelectStatus={handleTransitionSelect}
        borrowerName={loan.borrower?.name}
        principal={fmtCurrency(loan.principal)}
      />
    </PageLayout>
  );
}

export const LoanDetail = memo(LoanDetailInner);
